import {
  DEVICE_STORAGE_KEY,
  deviceTokenFor,
  PROOF_STORAGE_KEY,
  proofFor,
  StoredDevices,
  StoredProof,
  withDevice,
} from "./twoStep.utils";

/**
 * Where the browser keeps two-step sign-in state (localStorage, so new tabs
 * don't ask again): the 12-hour proof (cleared on sign-out) and the 30-day
 * "remember this computer" tokens per email (kept after sign-out, that's the
 * point). Every read/write is guarded: private windows may block storage.
 */

const read = <T>(key: string): T | null => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: unknown) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage blocked: the proof then lives only in memory for this page
  }
};

let memoryProof: StoredProof | null = null;

/** The proof to send with API calls (any signed-in person on this tab). */
export const currentProof = (): string | null => {
  const stored = read<StoredProof>(PROOF_STORAGE_KEY) || memoryProof;
  return stored ? proofFor(stored, stored.email) : null;
};

export const proofForEmail = (email: string) =>
  proofFor(read<StoredProof>(PROOF_STORAGE_KEY) || memoryProof, email);

export const saveProof = (email: string, proof: string, expiresAt: string) => {
  memoryProof = { email, proof, expiresAt };
  write(PROOF_STORAGE_KEY, memoryProof);
};

export const clearProof = () => {
  memoryProof = null;
  write(PROOF_STORAGE_KEY, null);
};

export const savedProofExpiry = (): string | null =>
  (read<StoredProof>(PROOF_STORAGE_KEY) || memoryProof)?.expiresAt || null;

export const deviceTokenForEmail = (email: string) =>
  deviceTokenFor(read<StoredDevices>(DEVICE_STORAGE_KEY), email);

export const saveDeviceToken = (
  email: string,
  token: string | null,
  expiresAt?: string | null,
) =>
  write(
    DEVICE_STORAGE_KEY,
    withDevice(
      read<StoredDevices>(DEVICE_STORAGE_KEY),
      email,
      token,
      expiresAt,
    ),
  );
